<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import i18next from 'i18next';
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import FundingService from '@/client/service/funding';
import type { CoveredCalendarInfo } from '@/client/service/funding';
import { useCalendarStore } from '@/client/stores/calendarStore';

const { t } = useTranslation('funding');

// Service instance
const fundingService = new FundingService();
const calendarStore = useCalendarStore();
const router = useRouter();

// State management
const loading = ref(true);
const processing = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

// Data state
const options = ref(null);
const status = ref(null);
const coveredCalendars = ref<CoveredCalendarInfo[]>([]);

// Computed properties
const hasFundingPlan = computed(() => status.value !== null);

const isActive = computed(() => status.value?.status === 'active');

const isPastDue = computed(() => status.value?.status === 'past_due');

const isSuspended = computed(() => status.value?.status === 'suspended');

const isCancelled = computed(() => status.value?.status === 'cancelled');

/**
 * A cancellation is scheduled but has not taken effect yet.
 *
 * The plan is still 'active' in this state, and legitimately so — it is paid
 * through the boundary — so `isCancelled` says nothing about it.
 */
const pendingCancellation = computed(() => Boolean(status.value?.cancelAt) && !isCancelled.value);

/**
 * The date coverage runs to, once anything has ended it. Null while the plan
 * is simply continuing.
 */
const accessEndsAt = computed(() => {
  if (pendingCancellation.value) {
    return status.value?.cancelAt ?? null;
  }
  return isCancelled.value ? status.value?.currentPeriodEnd ?? null : null;
});

// Nothing left to cancel once a cancellation is already scheduled.
const canCancel = computed(() =>
  hasFundingPlan.value && (isActive.value || isPastDue.value) && !pendingCancellation.value,
);

const currentAmountDisplay = computed(() => {
  if (!status.value) return '';
  return FundingService.formatCurrency(status.value.amount, status.value.currency);
});

/**
 * Get the display name for a covered calendar
 */
function getCalendarName(calendarId: string): string {
  const calendar = calendarStore.getCalendarById(calendarId);
  if (calendar) {
    return calendar.content(i18next.language).name || calendarId;
  }
  return calendarId;
}

/**
 * Format the per-calendar amount for display
 */
function formatCalendarAmount(amount: number): string {
  if (!status.value) return '';
  return FundingService.formatCurrency(amount, status.value.currency);
}

/**
 * Load funding options and current status
 */
async function loadData() {
  try {
    loading.value = true;
    [options.value, status.value] = await Promise.all([
      fundingService.getOptions(),
      fundingService.getStatus(),
    ]);

    if (status.value) {
      try {
        coveredCalendars.value = await fundingService.getCalendarsInFundingPlan();
      }
      catch (error) {
        console.error('Failed to load covered calendars:', error);
        coveredCalendars.value = [];
      }
    }
  }
  catch (error) {
    console.error('Failed to load funding data:', error);
    errorMessage.value = t('load_error');
  }
  finally {
    loading.value = false;
  }
}

/**
 * Cancel funding plan
 */
async function cancelFundingPlan() {
  if (!confirm(t('cancel_confirm'))) {
    return;
  }

  processing.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const success = await fundingService.cancel();

    if (success) {
      successMessage.value = t('cancel_success');
      await loadData();
    }
    else {
      errorMessage.value = t('cancel_error');
    }
  }
  catch (error) {
    console.error('Failed to cancel funding plan:', error);
    errorMessage.value = t('cancel_error');
  }
  finally {
    processing.value = false;
  }
}

/**
 * Open billing portal
 */
async function openBillingPortal() {
  try {
    // The portal returns to this leaf, resolved by name so no query from the
    // current page is inherited; the server pins the origin
    // (validateReturnUrlOrigin). A provider return URL addresses a leaf,
    // never a settings index (DEC-021).
    const returnUrl = window.location.origin + router.resolve({ name: 'funding_plan' }).href;
    const portalUrl = await fundingService.getPortalUrl(returnUrl);
    window.location.href = portalUrl;
  }
  catch (error) {
    console.error('Failed to get portal URL:', error);
    errorMessage.value = t('portal_error');
  }
}

/**
 * Format date for display
 */
function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString(i18next.language);
}

// Load data on mount
onMounted(async () => {
  await loadData();
});
</script>

<template>
  <section class="settings funding-plan-management">
    <div role="status" aria-live="polite">
      <div v-if="successMessage" class="success-message">{{ successMessage }}</div>
      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
    </div>

    <div v-if="loading" class="loading">{{ t("loading") }}</div>

    <div v-else class="funding-plan-content">
      <!-- No funding plan - Direct user to calendar management -->
      <div v-if="!hasFundingPlan && options?.enabled" class="no-funding-plan">
        <h2>{{ t("no_funding_plan") }}</h2>
        <p>{{ t("no_plan_redirect") }}</p>
      </div>

      <!-- Active funding plan - Show status and management -->
      <div v-if="hasFundingPlan" class="funding-plan-status">
        <h2>{{ t("current_funding_plan") }}</h2>

        <div class="status-card">
          <div class="status-row">
            <span class="label">{{ t("status_label") }}</span>
            <span :class="`status-badge status-${status.status}`">
              {{ t(`status_${status.status}`) }}
            </span>
          </div>

          <div class="status-row">
            <span class="label">{{ t("billing_cycle_label") }}</span>
            <span>{{ t(`billing_cycle_${status.billingCycle}`) }}</span>
          </div>

          <div class="status-row">
            <span class="label">{{ t("amount_label") }}</span>
            <span>{{ currentAmountDisplay }}</span>
          </div>

          <div class="status-row">
            <span class="label">{{ t("current_period_label") }}</span>
            <span>{{ formatDate(status.currentPeriodStart) }} - {{ formatDate(status.currentPeriodEnd) }}</span>
          </div>

          <div v-if="status.cancelledAt" class="status-row">
            <span class="label">{{ t("cancelled_at_label") }}</span>
            <span>{{ formatDate(status.cancelledAt) }}</span>
          </div>

          <div v-if="accessEndsAt" class="status-message info">
            {{ t("cancellation_info", { date: formatDate(accessEndsAt) }) }}
          </div>

          <div v-if="isPastDue" class="status-message warning">
            {{ t("past_due_warning") }}
          </div>

          <div v-if="isSuspended" class="status-message error">
            {{ t("suspended_message") }}
          </div>
        </div>

        <div class="funding-plan-actions">
          <button
            v-if="isActive || isPastDue"
            type="button"
            class="btn btn--secondary"
            @click="openBillingPortal"
          >
            {{ t("manage_payment_button") }}
          </button>

          <button
            v-if="canCancel"
            type="button"
            class="danger"
            :disabled="processing"
            @click="cancelFundingPlan"
          >
            {{ t("cancel_funding_plan_button") }}
          </button>
        </div>
      </div>

      <!-- Covered calendars list -->
      <div v-if="hasFundingPlan" class="covered-calendars">
        <h2>{{ t("covered_calendars_title") }}</h2>

        <div v-if="coveredCalendars.length === 0" class="covered-calendars-empty">
          <p>{{ t("no_covered_calendars") }}</p>
        </div>

        <ul v-else class="covered-calendars-list">
          <li v-for="item in coveredCalendars" :key="item.calendarId" class="covered-calendar-item">
            <span class="calendar-name">{{ getCalendarName(item.calendarId) }}</span>
            <span class="calendar-amount">{{ t("covered_calendar_amount", { amount: formatCalendarAmount(item.amount) }) }}</span>
          </li>
        </ul>
      </div>

      <!-- Funding disabled message -->
      <div v-if="!options?.enabled" class="funding-disabled">
        <p>{{ t("funding_disabled") }}</p>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.funding-plan-management {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

h2 {
  font-weight: 200;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

.loading {
  padding: 2rem;
  text-align: center;
  color: var(--pav-color-text-secondary);
}

.success-message {
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background-color: #f0fff0;
  border: 1px solid #73d873;
  color: #2a7d2a;
  border-radius: 4px;
}

.error-message {
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background-color: #fff0f0;
  border: 1px solid #d87373;
  color: #7d2a2a;
  border-radius: 4px;
}

.funding-plan-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.no-funding-plan {
  text-align: center;
  padding: 2rem;
}

.covered-calendars {
  .covered-calendars-empty {
    padding: 1rem;
    text-align: center;
    color: var(--pav-color-text-secondary);
  }

  .covered-calendars-list {
    list-style: none;
    padding: 0;
    margin: 0;

    .covered-calendar-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem;
      border: 1px solid var(--pav-color-border-primary);
      border-radius: 8px;
      background: var(--pav-color-surface-secondary);
      margin-bottom: 0.5rem;

      .calendar-name {
        font-weight: var(--pav-font-weight-medium);
        color: var(--pav-color-text-primary);
      }

      .calendar-amount {
        font-size: 0.875rem;
        color: var(--pav-color-text-secondary);
      }
    }
  }
}

.funding-plan-status {
  .status-card {
    padding: 1.5rem;
    border: 1px solid var(--pav-color-border-primary);
    border-radius: 8px;
    background: var(--pav-color-surface-secondary);

    .status-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid var(--pav-color-border-primary);

      &:last-child {
        border-bottom: none;
      }

      .label {
        font-weight: var(--pav-font-weight-medium);
        color: var(--pav-color-text-secondary);
      }

      .status-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 4px;
        font-size: 0.875rem;
        font-weight: var(--pav-font-weight-medium);

        &.status-active {
          background: #d4edda;
          color: #155724;
        }

        &.status-past_due {
          background: #fff3cd;
          color: #856404;
        }

        &.status-suspended, &.status-cancelled {
          background: #f8d7da;
          color: #721c24;
        }
      }
    }

    .status-message {
      margin-top: 1rem;
      padding: 0.75rem;
      border-radius: 4px;

      &.info {
        background: #d1ecf1;
        color: #0c5460;
      }

      &.warning {
        background: #fff3cd;
        color: #856404;
      }

      &.error {
        background: #f8d7da;
        color: #721c24;
      }
    }
  }

  .funding-plan-actions {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;

    button.danger {
      background: #dc3545;
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        background: #c82333;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

.funding-disabled {
  padding: 2rem;
  text-align: center;
  color: var(--pav-color-text-secondary);
}
</style>
